import React, { useState, useEffect } from 'react';
import { 
  X, Play, ChevronRight, Lock, 
  CheckCircle2, Star, Award, BookOpen, BarChart, 
  MessageSquare, ListVideo, DownloadCloud, ThumbsUp
} from 'lucide-react';
import { motion } from 'framer-motion';

const CoursePlayerView = ({ course, onClose }) => {
  // Estado para el video actual que se está reproduciendo
  const [currentVideo, setCurrentVideo] = useState(
    course.chapters && course.chapters.length > 0 ? course.chapters[0] : null
  );
  
  // Estado para simular el progreso (cuántas lecciones están desbloqueadas)
  const [unlockedIndex, setUnlockedIndex] = useState(0);

  // Datos locales para las reseñas de alumnos
  const studentReviews = [
    { name: "Carlos Ruiz", date: "Hace 2 días", comment: "La mejor explicación sobre Hooks que he visto. Muy directo al grano.", rating: 5 },
    { name: "Elena Sanz", date: "Hace 1 semana", comment: "Excelente calidad de audio y video. Los recursos descargables ayudan mucho.", rating: 5 }
  ];

  // Efecto para bloquear el scroll del fondo cuando el reproductor está abierto
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Cálculos de progreso
  const progress = Math.round(((unlockedIndex + 1) / course.chapters.length) * 100);
  const isFinished = progress === 100;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-[#050505] overflow-y-auto"
    >
      
      {/* HEADER DEL REPRODUCTOR */}
      <div className="sticky top-0 left-0 w-full p-6 flex justify-between items-center z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white transition-colors">
            <X size={24} />
          </button>
          <div className="hidden sm:block">
            <h2 className="text-white font-bold text-lg">{course.title}</h2>
            <p className="text-blue-500 text-[10px] font-bold uppercase tracking-widest">{currentVideo?.title}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
            <div className="hidden lg:flex flex-col items-end">
                <span className="text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-1">
                    Progreso: {progress}%
                </span>
                <div className="w-48 h-2 bg-emerald-900/20 rounded-full border border-emerald-500/20 p-[2px]">
                    <motion.div 
                      animate={{ width: `${progress}%` }}
                      className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)]"
                    />
                </div>
            </div>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-full font-bold text-xs uppercase tracking-tight hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20">
                <DownloadCloud size={16} /> Recursos
            </button>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="max-w-[1600px] mx-auto p-4 md:p-8 grid grid-cols-1 xl:grid-cols-12 gap-12">
        
        {/* LADO IZQUIERDO: VIDEO E INFO */}
        <div className="xl:col-span-8 space-y-12">
          
          {/* VIDEO FRAME */}
          <div className="aspect-video w-full bg-black rounded-[40px] overflow-hidden shadow-2xl border border-white/10 relative">
            <iframe
              className="w-full h-full"
              src={`${currentVideo?.videoUrl}?autoplay=0&modestbranding=1&rel=0`}
              title={currentVideo?.title}
              frameBorder="0"
              allowFullScreen
            ></iframe>
          </div>

          {/* GRID DE ESTADÍSTICAS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: BookOpen, label: "Módulos", val: course.chapters.length, col: "text-blue-400" },
                { icon: BarChart, label: "Nivel", val: "Intermedio", col: "text-blue-400" },
                { icon: Star, label: "Rating", val: "4.9 / 5.0", col: "text-yellow-500", fill: true },
                { icon: ThumbsUp, label: "Éxito", val: "98% Alumnos", col: "text-emerald-500" }
              ].map((item, i) => (
                <div key={i} className="bg-[#0F172A] p-4 rounded-3xl border border-white/5 flex items-center gap-3">
                   <item.icon className={item.col} size={20} fill={item.fill ? "currentColor" : "none"} />
                   <div>
                     <p className="text-[10px] text-slate-500 uppercase font-bold">{item.label}</p>
                     <p className="text-sm text-white font-bold">{item.val}</p>
                   </div>
                </div>
              ))}
          </div>

          {/* INSTRUCTOR Y CERTIFICADO */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
             <div className="bg-[#0F172A] p-6 rounded-[32px] border border-white/5 flex items-center gap-4">
                <img 
                  src={`https://ui-avatars.com/api/?name=${course.instructor}&background=3b82f6&color=fff`} 
                  className="w-14 h-14 rounded-2xl object-cover" 
                  alt="Docente"
                />
                <div>
                   <h4 className="text-lg font-bold text-white">{course.instructor}</h4>
                   <p className="text-xs text-slate-500 uppercase font-bold tracking-tighter">Instructor Verificado</p>
                </div>
             </div>

             <div className={`relative aspect-[1.8/1] rounded-[32px] border-2 p-6 flex flex-col justify-between overflow-hidden transition-all duration-700 ${
                 isFinished ? 'bg-emerald-500/5 border-emerald-500/50 backdrop-blur-xl' : 'bg-white/5 border-white/10 opacity-50'
             }`}>
                <div className="flex justify-between items-start relative z-10">
                   <div className={`w-8 h-8 border-2 rounded-lg flex items-center justify-center font-bold text-xs ${isFinished ? 'border-emerald-500 text-emerald-500' : 'border-white/20 text-white/20'}`}>Z.</div>
                   <Award size={20} className={isFinished ? 'text-emerald-500' : 'text-white/20'} />
                </div>
                <p className="text-sm font-black text-white relative z-10 leading-tight">{course.title}</p>
                {!isFinished && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center">
                    <Lock size={20} className="text-white/40" />
                  </div>
                )}
             </div>
          </div>

          {/* SECCIÓN DE RESEÑAS */}
          <div className="space-y-6 pt-10 border-t border-white/5">
             <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <MessageSquare size={22} className="text-blue-500" /> Opiniones de la comunidad
                </h3>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {studentReviews.map((rev, i) => (
                   <div key={i} className="bg-[#0F172A] p-6 rounded-[32px] border border-white/5 space-y-3">
                      <div className="flex justify-between items-start">
                         <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center font-bold text-blue-500 text-xs">{rev.name.charAt(0)}</div>
                            <div>
                              <p className="text-white text-sm font-bold">{rev.name}</p>
                              <p className="text-[10px] text-slate-500">{rev.date}</p>
                            </div>
                         </div>
                         <div className="flex text-yellow-500">
                            {[...Array(rev.rating)].map((_, s) => <Star key={s} size={10} fill="currentColor" />)}
                         </div>
                      </div>
                      <p className="text-slate-400 text-xs leading-relaxed italic">"{rev.comment}"</p>
                   </div>
                ))}
             </div>
          </div>
        </div>

        {/* LADO DERECHO: PLAYLIST / PLAN DE ESTUDIOS */}
        <div className="xl:col-span-4">
          <div className="bg-[#0F172A] border border-white/10 rounded-[40px] p-6 sticky top-28">
            <h3 className="text-xs font-black text-white uppercase tracking-[0.2em] mb-8 flex items-center gap-2">
                <ListVideo size={18} className="text-blue-500" /> Plan de Estudios
            </h3>

            <div className="relative space-y-3">
              <div className="absolute left-7 top-0 bottom-0 w-px bg-white/10 ml-[-0.5px]" />
              {course.chapters.map((chapter, index) => {
                const isLocked = index > unlockedIndex;
                const isActive = currentVideo?.id === chapter.id;

                return (
                  <button 
                    key={chapter.id} 
                    disabled={isLocked} 
                    onClick={() => setCurrentVideo(chapter)} 
                    className={`relative w-full flex items-center gap-5 p-4 rounded-2xl transition-all group ${isActive ? 'bg-blue-500/10 border border-blue-500/20' : 'hover:bg-white/5 border border-transparent'} ${isLocked ? 'opacity-30' : 'cursor-pointer'}`}
                  >
                    <div className={`relative z-10 w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center border transition-all ${isActive ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/40' : isLocked ? 'bg-black border-white/10 text-slate-600' : 'bg-black border-blue-500/30 text-blue-500'}`}>
                      {isLocked ? <Lock size={14} /> : isActive ? <Play size={14} fill="white" className="ml-0.5" /> : <CheckCircle2 size={16} />}
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className={`text-sm font-bold truncate ${isActive ? 'text-blue-500' : 'text-slate-300'}`}>{chapter.title}</h4>
                      <span className="text-[9px] font-bold text-slate-500 uppercase">{chapter.duration}</span>
                    </div>
                    <ChevronRight size={14} className={`transition-all ${isActive ? 'text-blue-500 translate-x-1' : 'text-white/10'}`} />
                  </button>
                );
              })}
            </div>

            {unlockedIndex < course.chapters.length - 1 && (
              <button 
                onClick={() => setUnlockedIndex(prev => prev + 1)}
                className="w-full mt-8 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20"
              >
                Siguiente Lección
              </button>
            )}
          </div>
        </div>

      </div>
    </motion.div>
  );
};

export default CoursePlayerView;